// NOTE: hooks
import { lazy, ReactNode, Suspense } from 'react'
import { useRoutes } from 'react-router-dom'
// NOTE: components
import Layout from './components/ui/Layout'
import PageSkeleton from './components/ui/PageSkeleton'
import AssessmentSkeleton from './pages/assessment/AssessmentSkeleton'
import CliniciansSkeleton from './pages/clinicians/CliniciansSkeleton'
import ContentsSkeleton from './pages/constents/ContentsSkeleton'
import ContentSkeleton from './pages/content/ContentSkeleton'
// NOTE: pages
const Home = lazy(() => import('./pages/home/Home'))
const Individuals = lazy(() => import('./pages/individuals/Individuals'))
const Business = lazy(() => import('./pages/business/Business'))
const Clinic = lazy(() => import('./pages/clinic/Clinic'))
const Clinicians = lazy(() => import('./pages/clinicians/Clinicians'))
const Clinician = lazy(() => import('./pages/clinician/Clinician'))
const AboutUs = lazy(() => import('./pages/aboutUs/AboutUs'))
const Landing = lazy(() => import('./pages/landing/Landing'))
const Assessment = lazy(() => import('./pages/assessment/Assessment'))
const Lifestyle = lazy(() => import('./pages/assessment/lifestyle/Lifestyle'))
const WheelOfLife = lazy(() => import('./pages/assessment/wheel-of-life/WheelOfLife'))
const Vitals = lazy(() => import('./pages/assessment/vitals/Vitals'))
const Contents = lazy(() => import('./pages/constents/Contents'))
const Content = lazy(() => import('./pages/content/Content'))
const Error = lazy(() => import('./pages/404'))

interface LoadPageProps {
	page: ReactNode
	fallback?: ReactNode
}

const LoadPage = ({ page, fallback }: LoadPageProps) => {
	return <Suspense fallback={fallback ?? <PageSkeleton />}>{page}</Suspense>
}

const App = () => {
	const routing = useRoutes([
		{
			element: <Layout />,
			children: [
				{
					index: true,
					element: <LoadPage page={<Home />} />
				},
				{
					path: 'individuals',
					element: <LoadPage page={<Individuals />} />
				},
				{
					path: 'business',
					element: <LoadPage page={<Business />} />
				},
				{
					path: 'clinic',
					element: <LoadPage page={<Clinic />} />
				},
				{
					path: 'clinicians',
					element: <LoadPage page={<Clinicians />} fallback={<CliniciansSkeleton />} />
				},
				{
					path: 'clinician/:domain',
					element: <LoadPage page={<Clinician />} />
				},
				{
					path: 'about-us',
					element: <LoadPage page={<AboutUs />} />
				},
				{
					path: 'get-tested',
					element: <LoadPage page={<Landing />} />
				},
				{
					path: 'assessment',
					element: <LoadPage page={<Assessment />} fallback={<AssessmentSkeleton />} />
				},
				{
					path: 'assessment/lifestyle',
					element: <LoadPage page={<Lifestyle />} fallback={<AssessmentSkeleton />} />
				},
				{
					path: 'assessment/wheel-of-life',
					element: <LoadPage page={<WheelOfLife />} fallback={<AssessmentSkeleton />} />
				},
				{
					path: 'assessment/vitals',
					element: <LoadPage page={<Vitals />} fallback={<AssessmentSkeleton />} />
				},
				{
					path: 'blogs',
					element: <LoadPage page={<Contents />} fallback={<ContentsSkeleton />} />
				},
				{
					path: 'content/:id/:title',
					element: <LoadPage page={<Content />} fallback={<ContentSkeleton />} />
				},
				{
					path: '*',
					element: <LoadPage page={<Error />} />
				}
			]
		}
	])

	return routing
}

export default App
